import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { supabase } from '../../config/supabase';
import { useAuth } from '../../hooks/useAuth';
import LoadingScreen from '../common/LoadingScreen';

const fetchLatestUserProfile = async (userId) => {
  if (!userId) return { profileData: null, subStatus: null };
  
  // Simple debounce mechanism to prevent rapid successive calls
  const cacheKey = `profile_${userId}`;
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) {
    const { data, timestamp } = JSON.parse(cached);
    // Use cached data if it's less than 5 seconds old
    if (Date.now() - timestamp < 5000) {
      console.log("AuthCallback: Using cached profile data");
      return data;
    }
  }
  
  try {
    console.log("AuthCallback: fetchLatestUserProfile called for userId:", userId);
    const { data, error: profileError } = await supabase
      .from('user_profiles')
      .select('*, quiz_completed, subscription_status, subscription_ends_at')
      .eq('id', userId)
      .single(); 

    if (profileError) {
      if (profileError.code === 'PGRST116') { 
        console.log("AuthCallback: fetchLatestUserProfile - No profile found (PGRST116) for userId:", userId);
        return { profileData: null, subStatus: null };
      }
      console.error('Error fetching latest user profile:', profileError);
      throw profileError; 
    }
    
    console.log("AuthCallback: fetchLatestUserProfile - Profile data fetched:", data);
    let subStatus = data?.subscription_status;
    if (data?.subscription_ends_at) {
      const endDate = new Date(data.subscription_ends_at);
      if (endDate < new Date()) {
        subStatus = 'expired';
      } else if (data?.subscription_status === 'cancelled') {
        // Cancelled subscription is still active until end date
        subStatus = 'active';
      }
    }
    
    const result = { profileData: data, subStatus };
    
    // Cache the result
    sessionStorage.setItem(cacheKey, JSON.stringify({
      data: result,
      timestamp: Date.now()
    }));
    
    return result;
  } catch (err) {
    console.error('Error in fetchLatestUserProfile catch:', err);
    return { profileData: null, subStatus: null }; 
  }
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation(); 
  const { session, loading: authLoading } = useAuth(); // Removed profile, subStatus from here to rely on fresh fetch
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState("Processing authentication...");
  const [localLoading, setLocalLoading] = useState(true);
  const [hasProcessed, setHasProcessed] = useState(false); // Prevent multiple executions

  useEffect(() => {
    console.log('🚀 AuthCallback: useEffect triggered');
    console.log('🚀 AuthCallback: Current URL:', window.location.href);
    console.log('🚀 AuthCallback: Pathname:', location.pathname);
    console.log('🚀 AuthCallback: Search params:', location.search);
    console.log('🚀 AuthCallback: Hash:', location.hash);
    
    // Only run AuthCallback logic if we're actually on the auth callback route
    if (location.pathname !== '/auth/callback' || hasProcessed) {
      console.log("AuthCallback: Not on callback route or already processed, skipping logic");
      setLocalLoading(false);
      return;
    }

    // Mark as processed immediately to prevent any re-runs
    setHasProcessed(true);

    const queryParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.substring(1));
    const fromPayment = queryParams.get('from_payment') === 'true';
    
    // Check for password recovery flow
    const isPasswordRecovery = queryParams.get('type') === 'recovery' || 
                              hashParams.get('type') === 'recovery';
    
    // Simplified email verification detection
    const isEmailVerification = !fromPayment && !isPasswordRecovery && (
      queryParams.has('access_token') || 
      queryParams.has('refresh_token') || 
      queryParams.get('type') === 'email' ||
      location.hash.includes('access_token') ||
      location.hash.includes('refresh_token')
    );

    console.log("AuthCallback: URL analysis:", {
      fromPayment,
      isPasswordRecovery,
      isEmailVerification,
      pathname: location.pathname
    });

    const handleRouting = async () => {
      console.log("AuthCallback: handleRouting called", {
        fromPayment, 
        isPasswordRecovery, 
        isEmailVerification, 
        hasCode: queryParams.has('code'),
        hasAccessToken: queryParams.has('access_token') || location.hash.includes('access_token'),
        url: window.location.href,
        search: location.search,
        hash: location.hash,
        allQueryParams: Object.fromEntries(queryParams.entries()),
        allHashParams: Object.fromEntries(hashParams.entries())
      });

      // PKCE/code flow - only call exchangeCodeForSession when code exists
      if (queryParams.has('code')) {
        console.log("AuthCallback: PKCE code flow detected, attempting code exchange...");
        setStatusMessage("Exchanging authentication code...");
        
        try {
          const { data, error } = await supabase.auth.exchangeCodeForSession();
          
          if (error) {
            console.error("AuthCallback: PKCE exchange failed:", error);
            setStatusMessage("The reset link could not be validated. Please request a new one.");
            setError("Invalid or expired reset link. Please request a new password reset.");
            setLocalLoading(false);
            return;
          }
          
          console.log("AuthCallback: PKCE exchange successful");
          setStatusMessage("Authentication successful, redirecting...");
          
          // After successful PKCE exchange, redirect to reset password (without tokens)
          if (isPasswordRecovery) {
            setLocalLoading(false);
            navigate('/reset-password', { replace: true });
            return;
          }
          
        } catch (err) {
          console.error("AuthCallback: PKCE exchange error:", err);
          setStatusMessage("Authentication error. Please try again.");
          setError("Authentication error. Please request a new reset link.");
          setLocalLoading(false);
          return;
        }
      }

      // Legacy token parameter flow (?token=...&type=recovery)
      if (queryParams.has('token') && isPasswordRecovery) {
        console.log("AuthCallback: Legacy token parameter flow detected");
        setStatusMessage("Processing authentication token...");
        
        try {
          // For legacy token flow, we need to call verifyOtp
          const token = queryParams.get('token');
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: 'recovery'
          });
          
          if (error) {
            console.error("AuthCallback: Token verification failed:", error);
            setStatusMessage("The reset link could not be validated. Please request a new one.");
            setError("Invalid or expired reset link. Please request a new password reset.");
            setLocalLoading(false);
            return;
          }
          
          console.log("AuthCallback: Token verification successful");
          setStatusMessage("Authentication successful, redirecting...");
          
          // After successful token verification, redirect to reset password
          setLocalLoading(false);
          navigate('/reset-password', { replace: true });
          return;
          
        } catch (err) {
          console.error("AuthCallback: Token verification error:", err);
          setStatusMessage("Authentication error. Please try again.");
          setError("Authentication error. Please request a new reset link.");
          setLocalLoading(false);
          return;
        }
      }

      // Legacy hash-token flow (access_token in hash with type=recovery)
      const hasLegacyAccessToken = location.hash.includes('access_token=') && 
                                  (isPasswordRecovery || location.hash.includes('type=recovery'));
      
      if (hasLegacyAccessToken) {
        console.log("AuthCallback: Legacy hash-token flow detected for password recovery");
        setStatusMessage("Processing authentication tokens...");
        
        // For legacy token password recovery, redirect to reset password (without tokens)
        setLocalLoading(false);
        navigate('/reset-password', { replace: true });
        return;
      }

      // Unknown/invalid callback - redirect to login
      if (isPasswordRecovery && !queryParams.has('code') && !queryParams.has('token') && !hasLegacyAccessToken) {
        console.log("AuthCallback: Password recovery callback without valid code, token, or access_token");
        setStatusMessage("Invalid reset link format. Please request a new one.");
        setError("Invalid reset link. Please request a new password reset from the login page.");
        setLocalLoading(false);
        navigate('/login', { replace: true });
        return;
      }

      setStatusMessage("Verifying account details..."); 

      if (authLoading) { 
        setStatusMessage("Waiting for authentication to initialize...");
        console.log("AuthCallback: authLoading is true, waiting...");
        return; 
      }

      // If this is email verification callback (not from payment), check if user needs quiz
      if (isEmailVerification) {
        setStatusMessage("Email verified successfully! Checking account status...");
        console.log("AuthCallback: Email verification detected, checking if user needs to complete quiz");
        
        // For email verification, keep the session and check if profile exists
        if (session?.user) {
          const { profileData } = await fetchLatestUserProfile(session.user.id);
          
          // If no profile exists, user needs to complete quiz
          if (!profileData) {
            setStatusMessage("Email verified! Redirecting to complete your personalized assessment...");
            console.log("AuthCallback: No profile found after email verification, redirecting to quiz");
            setLocalLoading(false);
            navigate('/quiz', { replace: true });
            return;
          }
          
          // If profile exists but quiz not completed, redirect to quiz
          if (profileData.quiz_completed !== true) {
            setStatusMessage("Email verified! Completing your personalized assessment...");
            console.log("AuthCallback: Profile exists but quiz not completed, redirecting to quiz");
            setLocalLoading(false);
            navigate('/quiz', { replace: true });
            return;
          }
          
          // If quiz is completed, proceed to subscription check
          console.log("AuthCallback: Quiz already completed, proceeding with normal flow");
        } else {
          // No session after email verification, redirect to login
          setStatusMessage("Email verified successfully! Please sign in to continue...");
          console.log("AuthCallback: No session after email verification, redirecting to login");
          setLocalLoading(false);
          navigate('/login', { 
            state: { 
              message: 'Email verified successfully! Please sign in to continue with your personalized plan.' 
            },
            replace: true
          });
          return;
        }
      }

      if (!session?.user) {
        setStatusMessage("No active session. Redirecting to login...");
        console.log("AuthCallback: No session.user after authLoading. Navigating to /login.");
        setLocalLoading(false); 
        navigate('/login');
        return;
      }
      
      console.log("AuthCallback: Session user confirmed:", session.user.id, "Attempting to fetch latest profile.");
      setStatusMessage("Fetching latest account details...");
      
      const { profileData: freshProfile, subStatus: freshSubStatus } = await fetchLatestUserProfile(session.user.id);
      
      if (fromPayment) {
          // Small delay for webhook, though freshProfile should ideally have latest.
          await new Promise(resolve => setTimeout(resolve, 500)); 
      }

      setLocalLoading(false); 

      console.log("AuthCallback: handleRouting decision point", {
        fromPayment,
        sessionUserId: session?.user?.id,
        freshProfileId: freshProfile?.id,
        freshProfileQuizCompleted: freshProfile?.quiz_completed, 
        typeOfQuizCompleted: typeof freshProfile?.quiz_completed, 
        freshSubStatus,
        completedQuizDataInSession: sessionStorage.getItem('completedQuizData') !== null 
      });

      try {
        // CRITICAL: Log the exact values being used in the condition
        console.log(`AuthCallback: Checking profile state: profile exists (${!!freshProfile}), quiz_completed value: ${freshProfile?.quiz_completed}, subscription_status: ${freshSubStatus}`);

        // If no profile exists at all, user must complete quiz first to create profile
        if (!freshProfile) {
          console.log("AuthCallback: No profile found - user must complete quiz to create profile");
          navigate('/quiz');
          return;
        }

        // If profile exists but quiz not completed, redirect to quiz
        if (freshProfile.quiz_completed !== true) {
          console.log("AuthCallback: Profile exists but quiz not completed. Navigating to /quiz. Quiz value:", freshProfile.quiz_completed);
          navigate('/quiz');
          return;
        }

        console.log("AuthCallback: Profile exists and quiz complete. Checking subscription status.");

        // Only allow conversation access if subscription is active
        if (freshSubStatus === 'active') {
          console.log("AuthCallback: Quiz complete and subscription active. Navigating to /conversation. Status:", freshSubStatus);
          navigate('/conversation');
          return;
        }
        
        console.log("AuthCallback: Quiz complete, but subscription not active in fetched data. Navigating to /subscribe. SubStatus:", freshSubStatus);
        let message = 'Please complete your subscription to access the platform.';
        if (freshSubStatus === 'expired') {
            message = 'Your subscription has expired. Please renew to continue.';
        }
        navigate('/subscribe', { state: { message } });
        return;

      } catch (e) {
        console.error('Auth callback routing error:', e);
        setError(e.message || 'An unexpected error occurred during authentication processing.');
        setLocalLoading(false); 
        navigate('/login', { 
          state: { 
            error: 'There was a problem processing your login. Please try again or contact support.' 
          } 
        });
      }
    };

    // Simple one-time execution with delay to avoid immediate re-execution
    setTimeout(handleRouting, 100);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname]); // Removed other dependencies to prevent re-runs

  // Only show AuthCallback UI if we're actually on the callback route
  if (location.pathname !== '/auth/callback') {
    return null;
  }

  if (error) {
    return (
      <div className="auth-error" style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Authentication Processing Error</h3>
        <p>{error}</p>
        <button onClick={() => navigate('/login')}>Go to Login</button>
      </div>
    );
  }
  
  if (localLoading && !error) { 
    return <LoadingScreen message={statusMessage} />;
  }

  return <LoadingScreen message="Finalizing and redirecting..." />; 
};

export default AuthCallback;
